<template>
  <label class="flex flex-col gap-1" :for="inputId">
    <span class="text-sm font-medium text-gray-700">
      {{ field.label }}<span v-if="isRequired" aria-label="required" class="text-red-600">*</span>
    </span>
    <select
      :id="inputId"
      v-model="model"
      :name="field.name"
      :aria-label="field.label"
      :aria-required="isRequired"
      :aria-invalid="!!errors"
      :aria-describedby="errors ? errorId : undefined"
      class="w-full rounded-none border-0 border-b border-black px-3 py-2 text-sm focus:border-koshee-blue focus:outline-none focus:ring-0 transition-colors bg-transparent"
      :class="{ 'border-rose-500': errors }"
    >
      <option disabled value="">
        {{ field.placeholder ?? "Select an option" }}
      </option>
      <option v-for="option in field.options" :key="option.value" :value="option.value">
        {{ option.label }}
      </option>
    </select>
    <span v-if="errors" :id="errorId" role="alert" aria-live="polite" class="text-xs text-rose-600">
      {{ errors }}
    </span>
  </label>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useField } from "vee-validate";
import type { RendererField } from "../../validation/schema";

const props = defineProps<{
  field: RendererField;
  rules: unknown;
}>();

const { value, errorMessage } = useField(props.field.name, props.rules);

const model = computed({
  get: () => value.value,
  set: (val) => {
    value.value = val;
  }
});

const errors = computed(() => errorMessage.value);
const inputId = computed(() => `field-${props.field.name}`);
const errorId = computed(() => `error-${props.field.name}`);
const isRequired = computed(() => {
  // Check if the field itself is marked as required
  return props.field.required === true;
});
</script>
